import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
} from '@mui/material';
import { CloudDownload as DownloadIcon } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#FFC658', '#FF6B9D'];

const SurveyStatistics = ({ open, onClose, survey }) => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentTab, setCurrentTab] = useState(0);

  useEffect(() => {
    if (open && survey) {
      fetchStatistics();
    }
  }, [open, survey]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/web/surveys/${survey.id}/statistics`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const data = await response.json();
      setStatistics(data);
    } catch (err) {
      setError('Ошибка загрузки статистики');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/web/surveys/${survey.id}/export`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `survey_${survey.id}_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      setError('Ошибка при скачивании файла');
      console.error(err);
    }
  };

  const renderQuestionChart = (question) => {
    const chartData = Object.entries(question.answers).map(([option, count]) => ({
      name: option.length > 30 ? option.substring(0, 30) + '...' : option,
      value: count,
    }));

    return (
      <Box key={question.id} mb={4}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {question.question}
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Тип: {question.type === 'single' ? 'Один вариант' : 'Несколько вариантов'} | 
            Всего ответов: {question.totalAnswers}
          </Typography>

          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} mt={2}>
            <Box flex={1}>
              <Typography variant="subtitle2" gutterBottom>
                Столбчатая диаграмма
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="value" fill="#8884d8" name="Количество ответов" />
                </BarChart>
              </ResponsiveContainer>
            </Box>

            <Box flex={1}>
              <Typography variant="subtitle2" gutterBottom>
                Круговая диаграмма
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Box>

          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Детальная статистика:
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Вариант ответа</TableCell>
                    <TableCell align="right">Количество</TableCell>
                    <TableCell align="right">Процент</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(question.answers).map(([option, count]) => (
                    <TableRow key={option}>
                      <TableCell>{option}</TableCell>
                      <TableCell align="right">{count}</TableCell>
                      <TableCell align="right">
                        {question.totalAnswers > 0
                          ? ((count / question.totalAnswers) * 100).toFixed(1)
                          : 0}
                        %
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Paper>
      </Box>
    );
  };

  const renderResponsesTable = () => {
    if (!statistics || !statistics.responses) return null;

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Пользователь</TableCell>
              <TableCell>Телефон</TableCell>
              <TableCell>Начат</TableCell>
              <TableCell>Завершен</TableCell>
              <TableCell>Статус</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statistics.responses.map((response) => (
              <TableRow key={response.id}>
                <TableCell>{response.user?.name || 'N/A'}</TableCell>
                <TableCell>{response.user?.phone || 'N/A'}</TableCell>
                <TableCell>
                  {new Date(response.startedAt).toLocaleString()}
                </TableCell>
                <TableCell>
                  {response.completedAt
                    ? new Date(response.completedAt).toLocaleString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {response.isCompleted ? 'Завершен' : 'В процессе'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Статистика опроса: {survey?.title}</Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadExcel}
            disabled={loading || !statistics}
          >
            Скачать Excel
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {statistics && !loading && (
          <>
            <Box mb={3}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Общая информация
                </Typography>
                <Box display="flex" gap={3}>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Всего ответов
                    </Typography>
                    <Typography variant="h4">{statistics.totalResponses}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Завершено
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {statistics.completedResponses}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      В процессе
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {statistics.totalResponses - statistics.completedResponses}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>

            <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} sx={{ mb: 2 }}>
              <Tab label="Графики и статистика" />
              <Tab label="Список ответов" />
            </Tabs>

            {currentTab === 0 && (
              <Box>
                {statistics.questions.map((question) => renderQuestionChart(question))}
              </Box>
            )}

            {currentTab === 1 && <Box>{renderResponsesTable()}</Box>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SurveyStatistics;
